import React, { useEffect, useState } from 'react';
import { animated } from 'react-spring';
import { Button, Col, Container, Row } from 'reactstrap';
import { sumBy } from 'lodash';

/* import TODO from '../../base/components/TODO'; */
import DevOnly from '../../base/components/DevOnly';
/* import BG from '../../base/components/BG'; */
import { GLCard, GLHorizontal, GLVertical, GLModalCard, GLModalBackdrop } from './GLCards';
import NGO from '../../base/data/NGO';
import Money from '../../base/data/Money';
import CharityLogo from '../CharityLogo';
import AdvertsCatalogue from '../campaignpage/AdvertsCatalogue';
import Misc from '../../base/components/Misc';

import Advert from '../../base/data/Advert';
import { getImpressionsByCampaignByCountry, getMainItem } from '../../base/data/ImpactPageData';
import printer from '../../base/utils/printer';

/**
 * DEBUG OBJECTS
 */

// import {TEST_CHARITY, TEST_CHARITY_OBJ, TEST_BRAND, TEST_BRAND_OBJ, TEST_CAMPAIGN, TEST_CAMPAIGN_OBJ} from './TestValues';
import { addAmountSuffixToNumber, space, uniq } from '../../base/utils/miscutils';

import Logo from '../../base/components/Logo';
import { getId } from '../../base/data/DataClass';
import PortalLink from '../../base/components/PortalLink';
import MDText from '../../base/components/MDText';

export class ImpactFilters {
	agency;
	brand;
	brand2;
	campaign;
	cid;
	/** @type {String} charity ID */
	ngo;
	impactdebit;
	start;
	end;
	status;
	q;
}


/**
 * The left or top half of the Impact Overview page. Contains the hero splash and run-downs of donation totals etc
 * @param {object} p
 * @param {object} p.brand Currently focused brand
 * @param {object} p.campaign Currently focused campaign
 * @param {object[]} p.charities List of charity objects associated with ads under the current focus
 * @param {object} p.impactDebits All impact debits associated with items under the current focus
 * @param {string} p.mainLogo Primary brand logo for the hero card
 * @param {string} p.totalString String representation of amount donated under current focus. Comes from ImpactPages.jsx
 * 
 * @returns {JSX.Element}
 */
function IOPFirstHalf({ mainItem, brand, campaign, charities, impactDebits, totalString, mainLogo }) {
	return <GLVertical>
		{/* top left corner - both top corners with basis 60 to line up into grid pattern */}
		<GLCard basis={campaign ? 80 : 60} className="hero-card">
			<div className="white-circle">
				<div className="content">
					<img className="logo" src={mainLogo} style={{ width: '6em', height: '6em'}}/>
					{brand && <DevOnly><PortalLink item={brand} /></DevOnly>}
					<br/>
					<h1 style={{marginBottom:0}}>{totalString}</h1>
					<h2>Donated</h2>
					<h5 className="m-0">With</h5>
					<img style={{width: '40%'}} src="/img/logo/Good-Loop-lockup.svg"/>
				</div>
			</div>
			<GLModalCard id="hero-card-modal" />
		</GLCard>

		{/* bottom left corner */}
		{campaign ? (
			<CampaignCharityDisplay charities={charities} impactDebits={impactDebits}/>
		) : (
			// Can't have in set - or CommonDivision wrapper messes up. Can't think of fix so just shove contents here for now
			<CharitiesCardSet charities={charities} impactDebits={impactDebits} mainItem={mainItem}/>
		)}
		<GLModalCard id="left-half" />
	</GLVertical>;
}


/**
 * Collapsed: Count of campaigns/sub-campaigns under the current focus
 * Expanded: List of campaigns
 * @param {obect} p
 * @param {object[]} p.brand Currently focused brand
 * @param {object[]} p.subBrandsDisplayable List of brands under the current focus which have impact debits attached
 * 
 * @returns {JSX.Element}
 */
function SubBrandsCard({ brand, subBrandsDisplayable: subBrands }) {
	if (!subBrands.length) return null;

	const count = subBrands.length;
	const noun = (count === 1) ? 'Brand' : 'Brands';

	const cardProps = {
		className: 'center-number',
		modalContent: <BrandList brand={brand} subBrands={subBrands} />,
		modalTitle: `${count} ${noun}`,
		modalId: 'right-half',
		modalClassName: 'list-modal'
	};

	return <GLCard {...cardProps}>
		<h2 className="m-0">{count}</h2>
		<h3>{noun}</h3>
	</GLCard>;
}


/**
 * Collapsed: Count of charities helped by group/brand/campaign
 * Expanded: List of charities helped
 * @param {object} p
 * @param {object[]} p.charities List of charity objects associated with ads under the current focus
 * @returns {JSX.Element}
 */
function CharitiesCard({ mainItem, charities }) {
	if (!charities.length) return null;

	const count = charities.length;
	const noun = (count === 1) ? 'Charity' : 'Charities';

	const cardProps = {
		className: 'center-number',
		modalContent: <CharityList mainItem={mainItem} charities={charities}/>,
		modalTitle: `${count} ${noun}`,
		modalId: 'right-half',
		modalClassName: 'list-modal'
	};

	return <GLCard {...cardProps}>
		<h2 className="m-0">{count}</h2>
		<h3>{noun}</h3>
	</GLCard>;
}


/**
 * Collapsed: Count of campaigns/sub-campaigns under the current focus
 * Expanded: List of campaigns
 * @param {obect} p
 * @param {object[]} p.subCampaignsDisplayable List of campaigns under the current focus which have impact debits attached
 * @param {object[]} p.brand Currently focused brand
 * @param {object[]} p.subBrands List of sub-brands under the current focus
 * @param {object[]} p.impactDebits ImpactDebits associated with campaigns under the current focus
 * @returns {JSX.Element}
 */
function SubCampaignsCard({ brand, subBrands, subCampaignsDisplayable: campaigns, impactDebits}) {
	if (!campaigns.length) return null;

	const count = campaigns.length;
	const noun = (count === 1) ? 'Campaign' : 'Campaigns';

	const cardProps = {
		basis: 10,
		className: 'center-number',
		modalContent: <CampaignList brand={brand} subBrands={subBrands} campaigns={campaigns} impactDebits={impactDebits}/>,
		modalTitle: `${count} ${noun}`,
		modalId: 'right-half',
		modalClassName: 'list-modal'
	};

	return <GLCard {...cardProps}>
		<h2 className="m-0">{count}</h2>
		<h3>{noun}</h3>
	</GLCard>;
}


/** Dead code: as of 22/02/2023, this card is being discussed if it should be kept or not */
function OffsetsCard() {
	return null;

	/*
	return <GLCard
		noPadding
		className="offset-card"
		basis={0}
		modalId="right-half"
		modalTitle="8.69T CO2E Offset"
		modalHeader={<CO2OffsetInfoHeader />}
		modalContent={<CO2OffsetInfo />}
		modalClassName="no-header-padding co2-offset"
	>
		<div className="offset-number px-3">
			<h3>8.69T CO2E OFFSET</h3>
		</div>
		<div className="carbon-neutral px-5 py-2">
			<img src="/img/Impact/Good-Loop_CarbonNeutralAd_Logo_Final-05.svg" className="w-100" />
		</div>
	</GLCard>;
	*/
}


/**
 * @param {object} p
 * @param {object[]} p.ads List of ads under the current focus
 * @param {boolean} [noPreviews] Don't show ad previews (ie for the small-card view)
 * @param {boolean} [unwrap] Don't wrap in card (for the modal view, which will be put in the existing modal card)
 * 
 * @returns {JSX.Element}
 */
function AdsCatalogueCard({ ads, campaign, unwrap }) {
	let showAds = ads.filter(ad => !Advert.hideFromShowcase(ad));

	// Just show the first ad - no controls etc needed, as clicking will open the modal
	const content = showAds.length ? (
		<AdvertsCatalogue ads={[showAds[0]]} noPreviews />
	) : (
		<h3>No ads yet!</h3>
	);

	if (unwrap) return content;

	const cardProps = {
		basis: (campaign && 70),
		className: 'ads-catalogue-card',
		modalId: 'full-page',
		modalContent: <AdvertsCatalogue ads={showAds} />,
		modalClassName: 'ads-catalogue-modal',
	};

	return <GLCard {...cardProps}>
		{content}
	</GLCard>;
}


/**
 * The right or bottom half of the Impact Overview page. Contains counts of ads,
 * campaigns, charities helped, types of Good-Loop products in play, and ad previews.
 * @param {object} p
 * @param {object} p.brand Currently focused brand
 * @param {object} p.campaign Currently focused campaign
 * @param {object[]} p.charities List of charity objects associated with ads under the current focus
 * @param {object} p.impactDebits All impact debits associated with items under the current focus
 * @param {string} p.mainLogo Primary brand logo for the hero card
 * @param {string} p.totalString String representation of amount donated under current focus
 * 
 * @returns {JSX.Element}
 */
const IOPSecondHalf = ({mainItem, ...baseObjects}) => {
	const { campaign, ads } = baseObjects;

	return <GLVertical>
		{/* top right corner */}
		{mainItem?.impactSettings?.csrHtml && <WiderCSR mainItem={mainItem} />}
		{!campaign && <GLHorizontal collapse="md" basis={60}>
			<GLVertical>
				<GLHorizontal>
					{baseObjects.subBrandsDisplayable?.length ? <SubBrandsCard {...baseObjects} /> : null}
					<CharitiesCard mainItem={mainItem} {...baseObjects} />
				</GLHorizontal>
				<SubCampaignsCard {...baseObjects} />
				<CountryViewsGLCard baseObjects={baseObjects} />
				<OffsetsCard />
			</GLVertical>
			<ContentListCard {...baseObjects } />
			<GLModalCard id="ads-for-good-modal" />
		</GLHorizontal>}

		{/* bottom right corner */}
		<GLVertical>
			{campaign && <CountryViewsGLCard basis={10} baseObjects={baseObjects}/>}
			<AdsCatalogueCard ads={ads} />
			{campaign && <SustainableGoalsCard basis={20} baseObjects={baseObjects}/>}
		</GLVertical>

		<GLModalCard id="right-half"/>
	</GLVertical>;
};


function WiderCSR({mainItem}) {
	if (!mainItem?.impactSettings?.csrHtml) return null;

	// HACK! extract e.g. background: url("https://example.com/myimage.jpg") cover
	let html = mainItem.impactSettings.csrHtml;
	let background;
	let m = html.match(/background:(.+)$/m);
	if (m) {
		background = "linear-gradient(90deg, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 40%, rgba(255,255,255,0) 100%), " + m[1].trim();
		html = html.substring(m[0].length).trim();
	}
	return <GLCard background={background}>
		<MDText source={html} style={{maxWidth: '40%'}} className="d-flex flex-column justify-contents-center align-items-center" />
	</GLCard>;
}

const SustainableGoalsCard = ({baseObjects}) => {
	const charities = baseObjects.charities;
	const unsdgs = charities ? uniq(charities.map(c => c.unsdg).filter(x => x)) : []; // no null entries or 0s

	if (!unsdgs) return null;

	return <GLCard className="boast">
		<div className="d-flex justify-content-center mb-2">
			<img src="/img/unsdg/unsdg.png" className="w-50"/>
		</div>
		<div className="justify-content-center d-flex align-items-center flex-wrap">
			{unsdgs.map(goal => <img key={goal} src={`/img/unsdg/goal-${goal - 1}.jpg`} className="goal-logo"/>)}
		</div>
	</GLCard>;
};

/**
 * @param {Object} p
 * @param {PromiseValue} p.pvBaseObjects See ImpactPageData.js:fetchImpactBaseObjects for resolved value.
 * @param {object} p.navToggleAnimation Params for animating nav bar (?)
 * @param {string} totalS
 */
const ImpactOverviewPage = ({pvBaseObjects, navToggleAnimation, ...props}) => {
	if (!pvBaseObjects.resolved) return <Misc.Loading text="Fetching impact data..." />;
	const baseObjects = pvBaseObjects.value;

	const mainItem = getMainItem(baseObjects);

	const { width } = navToggleAnimation;
	const style = { width, minWidth: width };

	return <>
		<div className="iview-positioner pr-md-1">
			<Container fluid className="iview-container">
				<animated.div className="impact-navbar-flow" style={style} />
				<GLVertical id="overview-first-card" className="w-100">
					<GLHorizontal collapse="md" className="iview-grid">
						<IOPFirstHalf mainItem={mainItem} {...baseObjects} {...props} />
						<IOPSecondHalf mainItem={mainItem} {...baseObjects} {...props} />
						<GLModalCard id="full-page"/>
					</GLHorizontal>
					<GLCard className="logos-display">
						<LogosDisplay {...baseObjects} />
					</GLCard>
				</GLVertical>
			</Container>
		</div>
		<GLModalBackdrop />
	</>;
};


const CampaignCharityDisplay = ({charities}) => {
	return <GLHorizontal basis={20}>
		{charities.map(charity => {
			return <GLCard key={charity.id}
				className="boast"
				modalContent={<CharityInfo charity={charity}/>}
				modalHeader={<CharityHeader charity={charity}/>}
				modalHeaderImg={charity.images}
				modalClassName="charity-info"
				modalId="right-half"
			>
				<img src={charity.logo} style={{width: '7rem'}}/>
				<br/>
				<h2>{Money.prettyStr(charity.dntnTotal)} Donated</h2>
				<br/>
				<h4>{NGO.displayName(charity)}</h4>
			</GLCard>;
		})}
	</GLHorizontal>;
};


/**
 * One, two or three charity cards (depending on donations found) to display below the hero splash
 * @param {object} p
 * @param {NGO[]} p.charities Charities donated to by the campaigns in scope.
 * @param {ImpactDebit[]} p.impactDebits The ImpactDebit objects representing in-scope donations.
 */
function CharitiesCardSet({mainItem, charities, impactDebits}) {
	// TODO Why do we have both of these? The user-visible one is never going to be seen...
	if (!charities?.length) return <DevOnly>No charities</DevOnly>;
	if (!charities.length) return <Misc.Loading text="Fetching donation data..." />;

	const [sortedCharities, setSortedCharities] = useState(charities);
	useEffect(() => {
		setSortedCharities(charities.toSorted((cA, cB) => {
			const dntnA = cA.dntnTotal || new Money(0);
			const dntnB = cB.dntnTotal || new Money(0);
			return -Money.compare(dntnA, dntnB);
		}));
	}, [charities]);

	console.log('sortedCharities', sortedCharities.map(c => `${c.id}: ${Money.prettyStr(c.dntnTotal || 0)}`));

	const topCharities = <GLHorizontal className="top-charities">
		{sortedCharities.slice(0, 3).map(charity => {
			const cid = getId(charity);
			return <CharityCard id={cid} key={cid} charity={charity} impactDebits={impactDebits} />;
		})}
	</GLHorizontal>;

	if (sortedCharities.length <= 3) return topCharities;

	const cardSetProps = {
		modalContent: <CharityList mainItem={mainItem} charities={sortedCharities}/>,
		modalTitle: `${sortedCharities.length} Charities`,
		modalId: 'right-half',
		modalClassName: 'list-modal'
	};

	return <GLVertical>
		{topCharities}
		<GLCard className="more-charities card-body" noPadding noGrow {...cardSetProps}>
			<h5>Plus {sortedCharities.length - 3} more</h5>
			<div className="flex-row">
				{sortedCharities.slice(3).splice(0, 6).map(charity => { // only show 6 charities = one row, to fit on page
					const cid = getId(charity);
					const cname = NGO.displayName(charity);
					return <img className="charity-link logo" key={cid} src={charity?.logo} title={cname} />;
				})}
			</div>
		</GLCard>
	</GLVertical>;
}


/**
 * 
*/
function CharityCard({charity}) {
	/*
	let headerStyle = charity.images? {
		backgroundImage: "url("+charity.images+")",
		backgroundPosition: 'center'
	} : null;
	*/

	const cardProps = {
		modalContent:<CharityInfo charity={charity}/>,
		modalHeader:<CharityHeader charity={charity}/>,
		modalHeaderImg:charity.images,
		modalClassName:"charity-info",
		modalId:"right-half"
	};

	return <GLCard className="charity-card" {...cardProps}>
		<img alt={charity.name} src={charity.logo} className="charity-logo"/>
		{/* This modal is similar (though not quite as well styled) as the one in the charity list on-click.
		Advantage: this uses vanilla bootstrap and is simpler.
		Disadvantage: Cannot be positioned easily. The GLCard modals allow modals to remain in flow with the page.
		To Do?: refactor to use vanilla bootstrap modals elsewhere.
		<Modal isOpen={show} toggle={() => setShow(!show)}
			// size="lg"
		>
			<div style={headerStyle}>
			<ModalHeader toggle={() => setShow(!show)}>
				{/* argh: modal-title is not filling the width so not centering
				<Center>
					<Circle padding="1rem" center>
						<CharityLogo charity={charity}/>
					</Circle>
				</Center>
				{/* <Misc.Logo service={C.app.id} url={logo} transparent={false} className="pull-left mr-1" />
				{' '}{title}
				{subtitle && <p className="my-4 login-subtitle">{subtitle}</p>}
			</ModalHeader>
			</div>
			<ModalBody>
				<CharityInfo charity={charity}/>
			</ModalBody>
		</Modal>*/}
		<h2 className="donation-total">
			{Money.prettyStr(charity.dntnTotal || 0)}
		</h2>
		<h3>raised</h3>
	</GLCard>;
}



function BrandLogo({item}) {
	return <Col md={1} xs={7} className="text-center">
		{item.branding?.logo ? <img src={item.branding.logo} className="logo"/> : <p>{item.name}</p>}
	</Col>;
}


function LogosDisplay({brand, subBrands}) {
	return <>
		<h3>Advertising that's a force for good</h3>
		<br/>
		<img src={brand?.branding?.logo} className="logo"/>
		<br/>
		<img className="a4glogo" src="/img/logo/Good-Loop-lockup.svg"/>
		<br/>
		<Row className="justify-content-center w-100">
			{subBrands.map(b => <BrandLogo item={b} key={b.id}/>)}
		</Row>
	</>;
}

/*
function ThisAdDoesGoodModal({ads, brand}) {
	const charity = TEST_CHARITY_OBJ;

	return <div className="bg-gl-background-default inmodal-wrapper p-5">
		<GLCard className="inmodal-content" noPadding>
			<BG src="/img/Impact/curves-background.svg" className="py-5 img-bg">
				<h3 className="text-white">1 This Ad Does Good Campaign</h3>
				<br/>
				<AdsCatalogueCard ads={ads} unwrap noPreviews />
				<br/>
				<Row className="text-center rates">
					<Col xs={4}>
						<p><TODO><b>XX%</b></TODO></p>
						<p>Viewability rate</p>
					</Col>
					<Col xs={4}>
						<p><b>XX%</b></p>
						<p>Click through rate</p>
					</Col>
					<Col xs={4}>
						<p><b>XX%</b></p>
						<p>Completed view rate</p>
					</Col>
				</Row>
			</BG>
			<br/>
			<h3><TODO>136,283 Trees Planted</TODO></h3>
			<br/>
			<h5>SUPPORTING</h5>
			<br/><br/>
			<p className="text-center">Reforestation projects in Madagascar, Kenya, and Mozambique</p>
			<br/>
			<Row className="w-50 mx-auto">
				<Col xs={6} className="d-flex flex-row align-items-center justify-content-center">
					<img src={brand.branding?.logo} className="logo"/>
				</Col>
				<Col xs={6} className="d-flex flex-row align-items-center justify-content-center">
					<TODO>which charity</TODO>
					<CharityLogo charity={charity}/>
				</Col>
			</Row>
			<br/>
		</GLCard>
	</div>;
}
*/

/**
 * TODO refactor to share code with ThisAdDoesGoodModal and BrandDonationInfo
 * @param {*} param0 
 * @returns 
 */
/*
const WatchToDonateModal = ({ads, brand}) => {
	const charity = TEST_CHARITY_OBJ;

	return <div className="bg-gl-background-default inmodal-wrapper p-5">
		<GLCard className="inmodal-content" noPadding >
			<BG src="/img/Impact/curves-background.svg" className="py-5 img-bg">
				<h3 className="text-white"><TODO>15</TODO> Watch To Donate Campaigns</h3>
				<br/>
				<AdsCatalogueCard ads={ads} unwrap noPreviews />
				<br/>
				<Row className="text-center rates">
					<Col xs={4}>
						<p>
							<b><TODO>XX%</TODO></b>
						</p>
						<p>Viewability rate</p>
					</Col>
					<Col xs={4}>
						<p>
							<b>XX%</b>
						</p>
						<p>Click through rate</p>
					</Col>
					<Col xs={4}>
						<p>
							<b>XX%</b>
						</p>
						<p>Completed view rate</p>
					</Col>
				</Row>
			</BG>
			<br/>
			<h3><TODO>£136,283 Donated</TODO></h3>
			<br/>
			<h5>INCLUDING</h5>
			<br/><br/>
			<p className="text-center"><TODO>Reforestation projects in Madagascar, Kenya, and Mozambique</TODO></p>
			<br/>
			<Row className="w-50 mx-auto">
				<Col xs={6} className="d-flex flex-row align-items-center justify-content-center">
					<img src={brand.branding?.logo} className="logo"/>
				</Col>
				<Col xs={6} className="d-flex flex-row align-items-center justify-content-center">
					<TODO>charity load</TODO>
					<CharityLogo charity={charity}/>
				</Col>
			</Row>
			<br/>
		</GLCard>
	</div>;
};
*/


// Keys matched to members of baseObjects
const contentTypes = {
	wtdAds: 'Watch to Donate',
	tadgAds: 'This Ad Does Good',
	etdAds: 'Engage to Donate',
	// greenTags: 'Green Ad Tag',
	// tasl: 'This Ad Supports Local'
};


function ContentListCard(baseObjects) {
	const activeTypes = Object.entries(contentTypes).map(([type, name]) => {
		const active = baseObjects[type]?.length; // ie true if there are entries in the "wtdAds" list etc
		return <div className="d-flex justify-content-start align-items-center">
				<img src={`/img/mydata/circle${active ? '' : '-no'}-tick.svg`} className="logo mr-3" />
				<h4 className="m-0 color-gl-dark-grey">{name}</h4>
		</div>;
	});

	return <GLCard>
		<div className="d-flex flex-column align-items-stretch justify-content-between h-100">
			<div className="my-3 text-center">
				<img className="w-75" src="/img/logo/Good-Loop-lockup.svg" />
				<h4 className="m-0">Ad Formats</h4>
			</div>
			{activeTypes}
		</div>
	</GLCard>;
}


/*
function AdsForGoodCTAHeader() {
	return <div className="bg-gl-impact-red pt-5 position-relative">
		<img src="/img/curves/curve-white.svg" className="w-100" />
		<img src="/img/Impact/images-combined.png" className="header-overlay" />
	</div>;
};
*/

/*
const AdsForGoodCTA = () => {
	const vertiser = TEST_BRAND_OBJ;

	return <div className="d-flex flex-column align-items-center justify-content-between h-100">
		<img src={vertiser.branding.logo} className="logo"/>
		<TODO>which brand</TODO>
		<img className="w-25" src="/img/gl-logo/AdsForGood/AdsForGood.svg"/>
		<h3>Discover our products and fund even more good causes</h3>
		<Button color="primary">Get in touch</Button>
	</div>
};
*/


/*
function CO2OffsetInfoHeader() {
	return <div className="bg-co2-offset pt-5 position-relative">
		<img src="/img/curves/curve-white.svg" className="w-100" />
		<img src="/img/green/hummingbird.png" className="header-overlay" />
	</div>;
};
*/

/*
function CO2OffsetInfo() {
	const vertiser = TEST_BRAND_OBJ;

	return <div className="d-flex flex-column align-items-center justify-content-between h-100">
		<img src={vertiser.branding.logo} className="logo"/>
		<TODO>which brand</TODO>
		<img className="w-25" src="/img/gl-logo/AdsForGood/AdsForGood.svg"/>
		<h4><TODO>Info about Green Ad Tag</TODO></h4>
		<h4><TODO>Info about Offset Project</TODO></h4>
		<h4><TODO>Cost of Offset - it only cost £X to offset</TODO></h4>
		<Button color="primary">Download offset certificate</Button>
	</div>
}
*/

function BrandListItem({item}) {
	const contents = <Col md={4} className="mt-3">
		<GLCard className={space('preview h-100', item._shouldHide && 'g-gl-light-red')} noMargin href={'/impact/view/brand/'+item.id}>
			{item && item.branding?.logo && <img src={item.branding.logo} className="logo"/>}
			{item._shouldHide && <p className="text-white">Normally hidden</p>}
			<p className="text-center">{item.name}</p>
		</GLCard>
	</Col>;
	return item._shouldHide ? <DevOnly>{contents}</DevOnly> : contents;
}

function BrandList({brand, subBrands}) {
	return <>
		<br/>
		<h5>Brands donating via Good-Loop Ads</h5>
		<p className="color-gl-red text-center">{brand.name} - All Campaigns</p>
		<br/>
		<Row>
			{subBrands.map(b => <BrandListItem item={b} key={b.id}/> )}
		</Row>
	</>;
}


function CharityList({mainItem, charities}) {
	return <>
		<br/>
		<h5>Charities supported by <span className="color-gl-red">{mainItem?.name}</span> via Good-Loop Ads</h5>		
		<br/>
		<Row>
			{charities.map((charity, i) => <Col key={i} md={4} className="mt-3">
				<GLCard className="preview h-100" noMargin
					modalContent={<CharityInfo charity={charity}/>}
					modalHeader={<CharityHeader charity={charity}/>}
					modalHeaderImg={charity.images}
					modalClassName="charity-info"
					modalId="left-half"
				>
					{charity && <CharityLogo charity={charity}/>}
					<p className="text-center">{NGO.displayName(charity)}</p>
				</GLCard>
			</Col>)}
		</Row>
	</>;
}


function CharityHeader({charity}) {
	return <div className="w-100 position-relative">
		<div className="white-circle">
			<CharityLogo charity={charity}/>
		</div>
	</div>;
}


function CharityInfo ({charity}) {
	return <div className="d-flex flex-column justify-content-center align-items-center charity-body">
		<h3>{NGO.displayName(charity)}</h3>
		<img className="w-25" src="/img/gl-logo/AdsForGood/AdsForGood.svg"/>
		<br/>
		<p className="p-5">
			<MDText source={NGO.extendedDescription(charity) || NGO.anyDescription(charity)}/>
		</p>
		{charity.url && <Button color="primary" href={charity.url} target="_blank">Visit site</Button>}
	</div>;
}


function CampaignList({campaigns, brand, subBrands}) {
	const allBrands = {};
	[brand, ...subBrands].forEach(b => {
		allBrands[b.id] = b;
	});

	return <>
		<h5>Campaigns run via Good-Loop Ads</h5>
		<p className="color-gl-red text-center">{brand.name} - All Campaigns</p>
		{campaigns.map(campaign => {
			const myBrand = allBrands[campaign.vertiser];
			const contents = <GLCard className={space('preview campaign mt-3', campaign._shouldHide && 'bg-gl-light-red')} noMargin key={campaign.id} href={"/impact/view/campaign/" + campaign.id}>
				<p className="w-75 text-left m-0">
					<div className="brand-name">{myBrand.name || campaign.vertiserName}</div>
					<div className="campaign-name">{campaign.name}</div>
				</p>
				{campaign._shouldHide && <p className="text-white">Normally Hidden</p>}
				<Logo item={myBrand} />
			</GLCard>;
			return campaign._shouldHide ? <DevOnly>{contents}</DevOnly> : contents;
		})}
	</>;
}


/** Round number to 3 significant figures and shorten with K/M/B suffix */
const shortNumber = (number) => (
	addAmountSuffixToNumber(printer.prettyNumber(number).replaceAll(',', ''))
);


const CountryViewsGLCard = ({basis, baseObjects}) => {
	if (!baseObjects) {
		console.warn('CountryViewsGLCard - no baseObjects');
		return null;
	}
	const impressionData = getImpressionsByCampaignByCountry({baseObjects, cutoff: 0.01}).value || {};
	// Prepare data for non-modal view - total impressions and countries
	const totalCountries = Object.keys(impressionData).filter(country => country !== 'unset').length;
	const impressions = sumBy(Object.values(impressionData), 'impressions'); // sum impressions over all regions
	const countryWord = (totalCountries === 1) ? 'country' : 'countries';

	const modalMapCardContent = <>
		<MapCardContent data={impressionData}/>
		<CampaignCountryList data={impressionData} />
	</>;

	// handle list of campaigns & countries inside modal

	return (
	<GLCard basis={basis}
		modalContent={modalMapCardContent}
		modalClassName="impact-map"
		modalId="right-half"
		className="center-number"
	>
		<h3>{shortNumber(impressions)} views in {totalCountries} {countryWord}</h3>
	</GLCard>
	);
};


/**
 * Stripped-down copy+paste from the Green Dashboard map widget.
 * TODO Make the aforementioned more generic and share code beyond just the mapdefs files?
 */
function MapCardContent({data}) {
	const [focusRegion, setFocusRegion] = useState('world'); // ID of currently focused country
	const [mapDefs, setMapDefs] = useState(); // JSON object with map paths and meta

	// Fetch the JSON with the map data for the current focus country
	useEffect(() => {
		// No mapdefs for this country? Return to world map and tell user
		const onError = () => {
			setFocusRegion('world');
		};

		fetch(`/js-data/mapdefs-${focusRegion}.json`).then((res) => {
			if (!res.ok) {
				onError();
				return;
			}
			res.json().then((json) => {
				setMapDefs(json);
			}).catch(onError);
		});
	}, [focusRegion]);

	return <SVGMap mapDefs={mapDefs} data={data} setFocusRegion={setFocusRegion} />;
}


function SVGMap({ mapDefs, data, setFocusRegion, setSvgEl}) {
	if (!mapDefs) return null;
	const loading = data === 'loading';

	let regions = [];
	Object.entries(mapDefs.regions).forEach(([id, props]) => {
		// Removed some code here which tried to normalise map entry key "UK" to ISO "GB" - seems the map has been fixed.
		let { impressions = 0, } = data?.[id] || {};

		props = { ...props, stroke: '#fff', strokeWidth: mapDefs.svgAttributes.fontSize / 10 };
		// Don't paint misleading colours on a map we don't have data for
		if (!loading) {
			// Define colours in CSS instead of code
			props.className = `region-path ${impressions ? 'impressions' : 'no-impressions'}`;
		}

		// Countries are clickable, sublocations aren't.
		if (setFocusRegion) {
			props.style = { cursor: 'pointer' };
			props.onClick = () => setFocusRegion(id);
		}

		// Tooltip on hover
		const title = loading ? null : <title>{props.name}: {impressions} views</title>;

		regions.push(
			<path key={`path-${id}`} data-id={id} {...props}>
				{title}
			</path>
		);
	});
	const svgRef = (element) => element && setSvgEl(element);

	return (
		<div className={space('map-container text-center', loading && 'loading')}>
			<svg className="map-svg" version="1.1" {...mapDefs.svgAttributes} xmlns="http://www.w3.org/2000/svg" ref={svgRef}>
				{regions}
			</svg>
			{loading && <Misc.Loading text={`Fetching map data for ${mapDefs.name}`} />}
		</div>
	);
}


function UnsetCountryWarning() {
	return <div className="row-aux unset-warning">
		Geolocation recording was implemented in April 2022.<br/>
		Older impressions will not be locatable.
	</div>;
}


/** Sorts the Object.entries() data we work with in CampaignCountryList */
const countryListComparator = ([,a], [,b]) => (b.impressions - a.impressions);


function CampaignCountryList({data}) {
	const regions = Object.entries(data).sort(countryListComparator);

	// If data has a region code "unset", push it to the back so it'll appear at the bottom of the country list.
	const unsetIndex = regions.findIndex(([key]) => key === 'unset');
	if (unsetIndex >= 0) {
		const [unsetKV] = regions.splice(unsetIndex, 1);
		regions.push(unsetKV);
	}

	// [['a', {x: 1, y: 2}], ['b', {x: 3, y: 4}], ['c', {x: 5, y: 6}]].map(([first, {x, y}]) => console.log(first, x, y))

	return regions.map(([regionCode, {impressions, campaignsInRegion}]) => {
		if (impressions === 0) return null;

		// Special display for the "unset" region
		let isUnset = false;
		let flag = null;
		if (regionCode === 'unset') {
			isUnset = true;
			regionCode = 'Unknown';
		} else {
			flag = <img src={`/img/country-flags/${regionCode.toLowerCase()}.svg`} className="country-flag" alt={`flag for ${regionCode}`} />;
		}

		return (
			<GLCard className={space('country-impressions p-2', isUnset && 'country-unset')} key={regionCode} noMargin noPadding>
				<div className="row-main">
					<div className="row-id">
						<span className="region-code">{regionCode}</span>
						{flag}
					</div>
					<div className="counts">
						<div className="count campaign-count">
							<div className="label">Campaigns</div>
							<div className="number">{campaignsInRegion}</div>
						</div>
						<div className="count impression-count">
							<div className="label">Views</div>
							<div className="number">{printer.prettyNumber(impressions, 21)}</div>
						</div>
					</div>
				</div>
				{isUnset && <UnsetCountryWarning />}
			</GLCard>
		);
	});
}

export default ImpactOverviewPage;
